/* eslint-disable class-methods-use-this */
import { Vue, Component, Watch } from 'vue-property-decorator'
import { namespace, Getter } from 'vuex-class'
import { processedUrl } from '@padlet/vivaldi-client'
import { getDisplayAttributes } from '@padlet/beethoven-client'
import { $ } from 'bits/jquery'

enum Fill {
  cover = 'cover',
  tile = 'auto',
  auto = 'auto',
}

@Component
class PageStylingMixin extends Vue {
  private latestBackgroudUpdateTime = new Date()

  @Getter private title
  @Getter private portrait
  @Getter private fontId
  @Getter private originalBackground
  @Getter private background
  @Getter private backgroundColor
  @Getter private backgroundLuminance
  @Getter private colorScheme
  @namespace('settings').Action private updateBackgroundMetadata

  @Watch('title')
  private setTitle (newValue): void {
    document.title = newValue
  }

  @Watch('portrait')
  private setIcon (newValue): void {
    const faviconElement = document.querySelector("link[rel='icon']") as HTMLLinkElement
    if (faviconElement) {
      faviconElement.href = newValue
    }

    const appleTouchIconElement = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement
    if (appleTouchIconElement) {
      appleTouchIconElement.href = newValue
    }
  }

  @Watch('fontId')
  private setFont (newFontId): void {
    $('[data-font]').attr('data-font', newFontId)
  }

  @Watch('backgroundLuminance')
  private setBackgroundLuminance (luminanceName): void {
    $('[data-behavior~="background-holder"]').attr('data-background-luminance', luminanceName)
  }

  @Watch('backgroundColor')
  private setHeaderColor (colorAsRgb): void {
    $('meta[name=theme-color]').attr('content', colorAsRgb)
    document.documentElement.style.backgroundColor = colorAsRgb
  }

  @Watch('colorScheme')
  private setColorScheme (newColorScheme): void {
    $('[data-color-scheme]').attr('data-color-scheme', newColorScheme)
  }

  private isUpdateStale (timestamp): boolean {
    return this.latestBackgroudUpdateTime !== timestamp
  }

  private async fetchBackgroundMetadata (url, timestamp): Promise<void> {
    const data = await getDisplayAttributes(url, { dpr: 1.0, locale: 'en' })
    const { image_color: dominantColourAsRgb, image_luminance: imageLuminance } = data
    if (this.isUpdateStale(timestamp)) return
    this.updateBackgroundMetadata({ dominantColourAsRgb, imageLuminance })
  }

  private setBackground (url, fill: string): void {
    $('[data-behavior~="background-holder"]').css({
      backgroundSize: Fill[fill],
      backgroundImage: `url(${url})`,
    })
  }

  @Watch('background')
  private updateBackground (newBackground, oldBackground): void {
    if (oldBackground.url === newBackground.url &&
        oldBackground.fill === newBackground.fill &&
        oldBackground.effect === newBackground.effect) return

    // Show tiny version of the new background first, in case background is huge
    const obscureBackgroundUrl = processedUrl(newBackground.url, { preset: 'obscure' })
    this.setBackground(obscureBackgroundUrl, Fill.cover)

    const thisUpdateTimestamp = new Date()
    this.latestBackgroudUpdateTime = thisUpdateTimestamp

    const transforms = {
      preset: 'background',
      width: window.screen.width,
      height: window.screen.height,
      effect: (newBackground.effect === 'blur') ? 'blur:10' : undefined,
    }
    const processedBackgroundUrl = processedUrl(newBackground.url, transforms)

    const image = new Image()
    image.onload = (): void => {
      if (this.isUpdateStale(thisUpdateTimestamp)) return

      let backgroundSize = Fill.auto
      if (newBackground.fill && newBackground.fill !== 'undefined') {
        backgroundSize = newBackground.fill
      } else if (image.width >= 800) {
        backgroundSize = Fill.cover
      }

      // Skip metadata update if we are reverting to original background since it is already present.
      if (this.originalBackground !== newBackground) {
        this.fetchBackgroundMetadata(newBackground.url, thisUpdateTimestamp)
      }

      this.setBackground(processedBackgroundUrl, backgroundSize)
    }
    image.src = processedBackgroundUrl
  }
}

export default PageStylingMixin
